import * as React from 'react'
import { createPortal } from 'react-dom'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import type { BillingSummaryDriver } from '../types'

interface Props {
  title: string
  description: string
  drivers: BillingSummaryDriver[]
  onClose: () => void
}

export default function BillingSummaryDriversModal({ title, description, drivers, onClose }: Props) {
  const { t } = useLocalize()
  const dialogRef = React.useRef<HTMLDialogElement>(null)
  const [search, setSearch] = React.useState('')
  const titleId = React.useId()

  React.useEffect(() => {
    const dialog = dialogRef.current!
    const previousFocus = document.activeElement as HTMLElement | null
    const previousOverflow = document.body.style.overflow
    dialog.showModal()
    document.body.style.overflow = 'hidden'
    return () => {
      dialog.close()
      document.body.style.overflow = previousOverflow
      previousFocus?.focus()
    }
  }, [])

  const query = search.trim().toLocaleLowerCase()
  const visibleDrivers = drivers.filter(d =>
    `${d.name ?? ''} ${d.surname ?? ''} ${d.driverId}`.toLocaleLowerCase().includes(query)
    || `${d.surname ?? ''} ${d.name ?? ''}`.toLocaleLowerCase().includes(query),
  )

  return createPortal(
    <dialog
      ref={dialogRef}
      aria-labelledby={titleId}
      className="tw-m-auto tw-w-[720px] tw-max-w-[calc(100vw-32px)] tw-rounded-lg tw-border-0 tw-bg-content-primary tw-p-0 tw-text-content-base tw-shadow-xl backdrop:tw-bg-black/50"
      onCancel={e => { e.preventDefault(); onClose() }}
      onKeyDown={e => {
        if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose() }
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="tw-flex tw-max-h-[80vh] tw-flex-col tw-p-5">
        <div className="tw-flex tw-items-start tw-justify-between tw-gap-4">
          <h2 id={titleId} className="tw-text-lg tw-font-semibold">{title}</h2>
          <button type="button" onClick={onClose} className="tw-shrink-0 tw-rounded tw-px-3 tw-py-1 hover:tw-bg-base-200">
            {t('common.close')}
          </button>
        </div>
        <p className="tw-my-3 tw-text-sm tw-text-content-secondary">{description}</p>
        <input
          type="search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          aria-label={t('billing.driverListSearch')}
          placeholder={t('billing.driverListSearch')}
          className="tw-mb-3 tw-w-full tw-rounded tw-border tw-border-base-300 tw-bg-content-primary tw-px-3 tw-py-2"
        />
        <p className="tw-mb-2 tw-text-sm" aria-live="polite">
          {query ? `${visibleDrivers.length} / ${drivers.length}` : drivers.length} {t('billing.driversCountUnit')}
        </p>
        <div className="tw-min-h-0 tw-overflow-y-auto">
          <table className="tw-w-full tw-text-left tw-text-sm">
            <thead className="tw-sticky tw-top-0 tw-bg-content-primary">
              <tr>
                <th className="tw-py-2 tw-pr-3">#</th>
                <th className="tw-py-2 tw-pr-3">{t('billing.col.driverName')}</th>
                <th className="tw-py-2">{t('billing.col.active')}</th>
              </tr>
            </thead>
            <tbody>
              {visibleDrivers.map((driver, index) => (
                <tr key={driver.driverId} className="tw-border-t tw-border-base-300">
                  <td className="tw-py-2 tw-pr-3">{index + 1}</td>
                  <td className="tw-py-2 tw-pr-3">
                    {[driver.surname, driver.name].filter(Boolean).join(' ') || `ID: ${driver.driverId}`}
                  </td>
                  <td className="tw-py-2">
                    {driver.isActive == null ? '—' : t(driver.isActive ? 'common.activeM' : 'common.inactiveM')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {visibleDrivers.length === 0 && <p className="tw-py-6 tw-text-center">{t('billing.driverListEmpty')}</p>}
        </div>
      </div>
    </dialog>,
    document.body,
  )
}
